import { yup } from '@framework/validation';
import { evaluateExpression } from '../schema/expressions/evaluate/evaluateExpression';
import { mapReferenceToPath } from '../schema/expressions/evaluate/mapReferenceToPath';
import { createEvaluationVariables } from '../schema/expressions/evaluate/variables/IEvaluationVariables';
import { collectExpressionReferences } from '../schema/expressions/references/collectExpressionReferences';
import { ICodeWidget } from '../schema/widgets/ICodeWidget';
import { IParentWidgetScope, IWidgetType } from '../schema/widgets/IWidgetType';
import { IWidgetOptionValue, Widget } from '../schema/widgets/Widget';
import { multiSelectOptionsObjectName } from '../schema/widgets/repository/inputs/multi-select/getMultiSelectOptionsObjectName';
import { WidgetConditionManager } from './WidgetConditionManager';
import { isParentWidgetScopeReadonly } from './utils/parent-scope/isParentWidgetScopeReadonly';
import { remapBooleanConditionReferencesUpwardInScope } from './utils/remapReferencesUpwardInScope';

/**
 * Funzione che applica la condizione di abilitazione/disabilitazione
 * allo schema così da poterlo gestire sia nel validatore
 * che in fase di compilazione
 */
export function setEnabledCondition<W extends Widget, S extends yup.AnySchema>(
  schema: S,
  widget: W,
  widgetType: IWidgetType<W>,
  parentScope: IParentWidgetScope = [],
) {
  // In Readonly, non applichiamo la disabilitazione in quanto comunque
  // il campo non è modificabile e il valore corretto è quello consolidato.
  if (isParentWidgetScopeReadonly(parentScope)) return schema.enabled(true);

  const conditionData = WidgetConditionManager.mergeWithScope(
    widget,
    'enabled',
    parentScope,
  );

  if (!conditionData) return schema;
  if (conditionData.condition.type === 'static') {
    return conditionData.condition.value
      ? schema.enabled(true)
      : schema.enabled(false).transform(() => schema.getDefault());
  }

  const { paths, condition, references } = conditionData;

  return schema.parentWhen(paths, (values, schema: any, context: any) => {
    const variables = createEvaluationVariables(references, values);
    const isEnabled = evaluateExpression(condition.expression, { variables });

    return isEnabled
      ? schema.enabled(true)
      : schema.enabled(false).transform(() => schema.getDefault());
  });
}

/**
 * Funzione che applica la condizione di abilitazione/disabilitazione
 * allo schema così da poterlo gestire sia nel validatore
 * che in fase di compilazione
 */
export function setOptionEnabledCondition<
  W extends ICodeWidget,
  S extends yup.AnySchema,
>(schema: S, widget: W, option: IWidgetOptionValue) {
  const condition = remapBooleanConditionReferencesUpwardInScope(
    { code: multiSelectOptionsObjectName(widget.code) },
    option.enabled,
  );

  if (!condition) return schema;
  if (condition.type === 'static') {
    return condition.value
      ? schema.enabled(true)
      : schema.enabled(false).transform(() => schema.getDefault()); // È "false"
  }

  const references = collectExpressionReferences(condition.expression);
  const paths = references.map(r => mapReferenceToPath(r));
  return schema.parentWhen(paths, (values, schema: any, context: any) => {
    const variables = createEvaluationVariables(references, values);
    const isEnabled = evaluateExpression(condition.expression, { variables });

    return isEnabled
      ? schema.enabled(true)
      : schema.enabled(false).transform(() => schema.getDefault());
  });
}
